import { useLocation } from 'react-router-dom';
import { withErrorBoundary } from 'react-error-boundary';
import { useCallback, useMemo, useRef, useState } from 'react';

import cs from 'classnames';
import { addYears, subYears } from 'date-fns';

import { ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { DEVICE_SIZES } from 'lib/hooks/useWindowSize';
import { useUIContext } from 'lib/context/UIContext/UIContext';

import Icon from '../Icon/Icon';
import Hint from '../shared/Hint';
import ClearIcon from '../shared/ClearIcon';
import { default as Header } from '../shared/Header';
import { formatShortWeekday } from '../Calendar/lib/formatShortWeekday';

import { getDateFormat } from './lib/formatDates';
import { validateDate } from './lib/validateDate';
import { getDateAriaLabels } from './lib/ariaLabels';
import { useYearChange } from './hooks/useYearChange';
import { parseDateValue } from './lib/parseDateValue';
import { useCalendarOpen } from './hooks/useCalendarOpen';
import { isRolledTypedDate } from './lib/isRolledTypedDate';
import { getPickerComponent } from './lib/getPickerComponent';
import { useCalendarPosition } from './hooks/useCalendarPosition';
import { useSeparatorKeyDown } from './hooks/useSeparatorKeyDown';
import { useRejectedDateReset } from './hooks/useRejectedDateReset';
import { useOutsideCalendarClose } from './hooks/useOutsideCalendarClose';
import { displayValue, pickerDateToUtc, toDateOnlyString } from './lib/pickerValue';

import './lib/DateInputPicker.scss';
import 'react-date-picker/dist/DatePicker.css';
import 'react-datetime-picker/dist/DateTimePicker.css';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css';

/**
 * @param {import('./lib/propTypes').DatePickerProps} props
 */

const DateInputPicker = (props) => {
  const {
    value,
    onChange = () => {},
    isTime = false,
    isRange = false,
    isTimeRange = false,
    isDateOnly = false,
    isTimezone = isTime || isTimeRange || false,
    isSeconds = false,
    minDate,
    maxDate,
    disabled = false,
    buttonClassName,
    parentClassName,
    isButtonTextWhite,
    isClearable = true,
    isIcon = true,
    iconIsRight = false,
    isCompact = false,
    showCalendarWeekNumbers = false,
    hideCalendar = false,
    openCalendarOnFocus,
    onCalendarToggle,
    position = 'left',
    error = null,
    timezone: timezoneProp = null,
    ...headerProps
  } = props;

  const calendarRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isOpen, setIsOpen] = useCalendarOpen(onCalendarToggle);
  const isValue = ia(value) ? Boolean(value?.[0]) : Boolean(value);
  const { pathname } = useLocation();
  const isSelfScheduling = pathname.startsWith('/book');

  const timezoneFromHook = useTimezone({ enabled: !timezoneProp && !isSelfScheduling });
  // An explicit prop wins over the /book browser-timezone fallback: self-scheduling values
  // are encoded in the practice timezone, and browser-zone parsing can shift them a day.
  const timezone =
    timezoneProp ||
    (isSelfScheduling ? Intl.DateTimeFormat().resolvedOptions().timeZone : timezoneFromHook);

  const [activeStartDate, setActiveStartDate] = useState(() => {
    if (!isValue) return new Date();
    const raw = ia(value) ? value[0] : value;
    return parseDateValue(raw, timezone) || new Date();
  });
  const [activeView, setActiveView] = useState('month');
  const rejectedDate = useRejectedDateReset();

  const { device } = useUIContext();
  const isDesktopDevice = device === DEVICE_SIZES.LAPTOP || device === DEVICE_SIZES.DESKTOP;
  const shouldOpenOnFocus = openCalendarOnFocus ?? (isDesktopDevice || !isIcon);

  const resolvedMinDate = useMemo(() => minDate ?? subYears(new Date(), 150), [minDate]);
  const resolvedMaxDate = useMemo(() => maxDate ?? addYears(new Date(), 150), [maxDate]);

  const resolvedIsCompact =
    isCompact || device === DEVICE_SIZES.MOBILE || device === DEVICE_SIZES.TABLET_SM;

  const DatePickerWrapper = getPickerComponent({ isTime, isRange, isTimeRange, isTimezone });

  const onCalendarClose = useCallback(() => {
    setIsOpen(false);
    setActiveStartDate(value ? parseDateValue(value, timezone) || new Date() : new Date());
    setActiveView('month');
  }, [value, timezone, setIsOpen]);

  useCalendarPosition({ calendarRef, isOpen, onClose: onCalendarClose, position });

  useYearChange({ value, isRange, isOpen, calendarRef });

  const shouldCloseCalendar = useOutsideCalendarClose({ calendarRef, isOpen });

  const separatorHandlers = useSeparatorKeyDown();

  const onResetDate = () => {
    onChange(null);
    setActiveStartDate(new Date());
    setActiveView('month');
  };

  const handleChange = (date) => {
    if (!date) {
      onChange(null);
      setActiveStartDate(new Date());
      return;
    }
    if (isRange || isTimeRange) {
      const [startDate, endDate] = date;
      onChange([pickerDateToUtc(startDate, timezone), pickerDateToUtc(endDate, timezone)]);
      setActiveStartDate(startDate || new Date());
      return;
    }
    if (isRolledTypedDate(date, wrapperRef.current)) {
      (props.onInvalidChange ?? rejectedDate.markRejected)();
      return;
    }
    onChange(isDateOnly ? toDateOnlyString(date) : pickerDateToUtc(date, timezone));
    setActiveStartDate(date);
  };

  const pickerValue = useMemo(
    () => displayValue(value, timezone, isRange || isTimeRange),
    [ia(value) ? `${value?.[0]}|${value?.[1]}` : value, timezone, isRange, isTimeRange]
  );

  return (
    <div
      ref={wrapperRef}
      onBlur={rejectedDate.onBlur}
      className={cs(
        'custom-picker w-full',
        parentClassName,
        disabled && 'cursor-not-allowed select-none'
      )}
      data-field-error={error ? '' : undefined}
      {...separatorHandlers}>
      <Header {...headerProps} />
      <DatePickerWrapper
        key={rejectedDate.resetKey}
        isOpen={isOpen}
        isCalendarOpen={isOpen}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={onCalendarClose}
        shouldCloseCalendar={shouldCloseCalendar}
        shouldCloseWidgets={shouldCloseCalendar}
        disableCalendar={hideCalendar && !isIcon}
        calendarProps={{
          inputRef: calendarRef,
          activeStartDate: validateDate(activeStartDate),
          onActiveStartDateChange: ({ activeStartDate }) => setActiveStartDate(activeStartDate),
          view: activeView,
          onViewChange: ({ view }) => setActiveView(view),
          className: cs(
            'mt-2 is-border origin-top animate-float-in motion-reduce:animate-none',
            resolvedIsCompact && 'is-compact'
          ),
          prev2Label: null,
          next2Label: null,
          prevLabel: <Icon icon="chevron-left-linear" size={16} />,
          nextLabel: <Icon icon="chevron-right-linear" size={16} />,
          formatShortWeekday,
          showWeekNumbers: showCalendarWeekNumbers
        }}
        disabled={disabled}
        format={getDateFormat({ isTime, isTimeRange, isSeconds })}
        calendarIcon={isIcon && <Icon icon="calendar-mono" size={20} />}
        clearIcon={
          <ClearIcon
            isClearable={isClearable ? isValue : false}
            value={value}
            onClick={onResetDate}
            color={isButtonTextWhite ? 'white' : 'neutral'}
            isValue={isValue}
          />
        }
        disableClock
        dayPlaceholder="dd"
        monthPlaceholder="mm"
        yearPlaceholder="yyyy"
        {...getDateAriaLabels(headerProps.label)}
        locale="en-US"
        {...props}
        openCalendarOnFocus={shouldOpenOnFocus}
        openWidgetsOnFocus={shouldOpenOnFocus}
        id="date-input-picker"
        className={cs(
          isValue && 'filled',
          !iconIsRight && 'iconIsRight',
          isTimeRange && !isOpen && 'closed',
          pathname === '/iframe.html' && 'isDocumentation',
          isButtonTextWhite && 'white-text',
          buttonClassName
        )}
        minDate={resolvedMinDate}
        maxDate={resolvedMaxDate}
        onChange={(date) => handleChange(date)}
        value={pickerValue}
      />
      <Hint hint={error} error={error} icon={false} />
    </div>
  );
};

export default withErrorBoundary(DateInputPicker);
